
// Labor-Deploy: Gitea Actions lab-portal-ui.yml (ANDREAS_LABOR_BEFEHLE Abschnitt 10)
import { BotStatus, Lead } from './types';

/**
 * Kundensichtbare Marken- und Produktnamen (Quelle der Wahrheit).
 * ENTSCHEIDUNG 30.06.2026 (Orbis Produktmodell): Firma = ARKORBIT, Produkt = Orbis (Marketing „Orbis OS").
 * Tarife: Orbis Professional / Orbis Premium / Orbis Enterprise.
 * Details/kanonisch: docs/intern/RECHTSTEXTE_KONSISTENZ.md → „Orbis Produktmodell".
 * Marke „Orbis": Markenanmeldung ausstehend (ÖPA/Reinisch) — kein ®.
 */
export const BRAND_FIRMA = 'ARKORBIT AI Austria';
export const BRAND_PORTAL = 'ARKORBIT Portal';

/** Produkt (Plattform) — Self-Hosted Kanzlei-Umgebung auf dediziertem Kunden-Server (Edge). */
export const PRODUCT_NAME = 'Orbis';
export const PRODUCT_NAME_MARKETING = 'Orbis OS';

/** Standard-Tarife (einzige Standard-Angebote) + Enterprise. */
export const TARIF_PROFESSIONAL = 'Orbis Professional';
export const TARIF_PREMIUM = 'Orbis Premium';
export const TARIF_ENTERPRISE = 'Orbis Enterprise';

/** Feature-Bausteine innerhalb von Orbis (kundensichtbar). */
export const FEATURE_DIKTAT = 'Diktat & Transkription';   // Baseline in beiden Standard-Tarifen
export const FEATURE_LITIGATION = 'Litigation Engine';    // nur Premium

/**
 * Kundensichtbare Produkt-/Tarifnamen für Produktseiten und Portal (Orbis Produktmodell 30.06.2026).
 * PRODUCT_STANDARD = Professional-Seite (Basis: Diktat, ARKO, BRAIN),
 * PRODUCT_PREMIUM  = Premium-Seite (zusätzlich Litigation Engine).
 * Für neue Stellen bevorzugt TARIF_* / FEATURE_* verwenden.
 */
export const PRODUCT_STANDARD = TARIF_PROFESSIONAL;
export const PRODUCT_PREMIUM = TARIF_PREMIUM;

/** Kanonische Produktseiten-Routen (an Tarifnamen ausgerichtet). */
export const ROUTE_PRODUCT_PROFESSIONAL = '/produkt/orbis-professional';
export const ROUTE_PRODUCT_PREMIUM = '/produkt/orbis-premium';
/** @deprecated Alias auf die kanonischen Routen (Altimporte). */
export const ROUTE_PRODUCT_DIKAT = ROUTE_PRODUCT_PROFESSIONAL;
export const ROUTE_PRODUCT_STRATEGIE = ROUTE_PRODUCT_PREMIUM;

/**
 * Legacy-URLs → Weiterleitung in App.tsx. Alte Produktlinks (Diktat/Strategie, Orbit Legal AI) leiten auf Orbis Professional bzw. Premium.
 */
export const LEGACY_ROUTES_TO_PROFESSIONAL = [
  '/produkt/orbis-diktat',
  '/produkt/arkorbit-diktat',
  '/produkt/orbit-legal-ai',
  '/diktat',
  '/orbit-legal-ai',
];
export const LEGACY_ROUTES_TO_PREMIUM = [
  '/produkt/orbis-strategie',
  '/produkt/arkorbit-strategie',
  '/produkt/mike-ross',
  '/strategie',
  '/mike-ross',
];

/**
 * Kundensichtbare Portal-Bausteine von Orbis (Quelle der Wahrheit für die Produktseiten).
 * Icons werden in den Komponenten gemappt (constants.ts bleibt JSX-frei).
 * Professional zeigt die Basis-Bausteine, Premium zusätzlich die Litigation Engine.
 */
export interface OrbisPortalFeature {
  id: string;
  name: string;
  tag: string;
  desc: string;
}

/** Basis-Bausteine (in Professional UND Premium enthalten). */
export const ORBIS_PORTAL_FEATURES: OrbisPortalFeature[] = [
  {
    id: 'portal',
    name: 'Kanzlei-Portal',
    tag: 'Ein Login',
    desc: 'Dashboard, Mandatsübersicht und alle Werkzeuge unter einer sicheren Anmeldung – Ihre Kanzlei an einem Ort, auf Ihrem eigenen Server.',
  },
  {
    id: 'arko',
    name: 'ARKO – KI-Rechtsassistent',
    tag: 'Chat mit Mandatskontext',
    desc: 'Rechtsnaher Assistent direkt im Portal: recherchiert, strukturiert und entwirft mit Blick auf Ihr Mandat. Ein Werkzeug – kein Ersatz für die anwaltliche Prüfung.',
  },
  {
    id: 'onlyoffice',
    name: 'OnlyOffice',
    tag: 'Dokumente im Browser',
    desc: 'Word-naher Editor direkt im Portal: Aktenvermerke, Verträge und Schriftsätze bearbeiten und speichern, ohne Export zu Dritten.',
  },
  {
    id: 'nextcloud',
    name: 'Nextcloud',
    tag: 'Sichere Ablage',
    desc: 'Verschlüsselte Dateiablage mit Mandantenordnern auf Ihrem eigenen Server – DSGVO-konform, ohne US-Cloud.',
  },
  {
    id: 'matrix',
    name: 'Matrix',
    tag: 'Team & Diktat-Upload',
    desc: 'Ende-zu-Ende verschlüsselte Team-Kommunikation und Diktat-Upload per Messenger – Self-Hosted in der EU.',
  },
  {
    id: 'transkription',
    name: 'Diktat & Transkription',
    tag: 'Sprache zu Text',
    desc: 'Whisper wandelt Sprachnachrichten in Text und fertige Aktenvermerk-PDFs – ein Baustein von Orbis, kein Fremdsystem.',
  },
  {
    id: 'lexcore',
    name: 'LEXCORE',
    tag: 'Österreichisches Recht',
    desc: 'Kuratierter Rechtskern aus Normen und Judikatur (RIS-basiert) – ehrlich im Ausbau, jede zitierte Norm mit Beleg.',
  },
  {
    id: 'brain',
    name: 'BRAIN / Kanzlei-Wissen',
    tag: 'Ihr Gedächtnis',
    desc: 'Durchsuchbares Kanzlei-Wissen auf Vektor-Basis – Ihre Dokumente und Präzedenzfälle bleiben auf Ihrem Server.',
  },
];

/** Zusatz-Baustein nur in Orbis Premium. */
export const ORBIS_LITIGATION_FEATURE: OrbisPortalFeature = {
  id: 'litigation',
  name: 'Litigation Engine',
  tag: 'Nur Premium',
  desc: 'Strategische Fallanalyse und Schriftsatz-Vorbereitung: quellengebundene KI-Analyse auf LEXCORE + OGH-Judikatur → DOCX/PDF mit verpflichtender anwaltlicher Prüfung.',
};

/** Einheitliche EUR-Formatierung (de-AT, ohne Nachkommastellen) für Produktseiten. */
export const formatEur = (amount: number): string =>
  new Intl.NumberFormat('de-AT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(amount);

// SYSTEM CONFIGURATION — Preise (Orbis Produktmodell, ENTSCHEIDUNG 30.06.2026)
export const SETUP_FEE = 1990;               // Einmalig je Kunde/Instanz (Professional + Premium)
export const MONTHLY_FEE_PROFESSIONAL = 1990; // Orbis Professional (5 Sitze inkl., ohne Litigation Engine)
export const MONTHLY_FEE_PREMIUM = 4990;      // Orbis Premium (10 Sitze inkl., mit Litigation Engine)
export const ADDON_SEAT_FEE = 189;            // Zusatzsitz/Monat/Sitz, bis Max 20 pro Instanz
export const SEATS_PROFESSIONAL = 5;          // inkl. Sitze Professional
export const SEATS_PREMIUM = 10;              // inkl. Sitze Premium
export const MAX_SEATS_PER_INSTANCE = 20;     // >20 → nur Orbis Enterprise

// ARKORBIT BRAIN (relative URLs – nginx proxied, kein CORS)
export const BRAIN_INGEST_URL  = '/webhook/brain-ingest';
export const BRAIN_UPLOAD_URL  = '/webhook/brain-upload';
export const QDRANT_PROXY_URL  = '/api/qdrant/collections';

// --- STRIPE PAYMENT LINKS (je Tarif: ein Link = Setup einmalig + monatliches Abo im Checkout) ---
// Optional: in .env überschreiben (VITE_STRIPE_LINK_*), sonst Defaults aus Stripe Dashboard.
const STRIPE_DEFAULT = {
    STARTER: 'https://buy.stripe.com/eVqdR32nLaEu0HlgBNb3q04',
    PREMIUM: 'https://buy.stripe.com/14A4gt4vT5kadu7adpb3q05',
    ENTERPRISE: 'https://buy.stripe.com/eVq14h1jH8wm4XBfxJb3q06',
} as const;

export const STRIPE_LINKS = {
    STARTER: (import.meta.env.VITE_STRIPE_LINK_STARTER as string | undefined)?.trim() || STRIPE_DEFAULT.STARTER,
    PREMIUM: (import.meta.env.VITE_STRIPE_LINK_PREMIUM as string | undefined)?.trim() || STRIPE_DEFAULT.PREMIUM,
    ENTERPRISE: (import.meta.env.VITE_STRIPE_LINK_ENTERPRISE as string | undefined)?.trim() || STRIPE_DEFAULT.ENTERPRISE,
} as const;

/** SessionStorage: nach Stripe-Zahlung Hinweis auf Login-Seite (App setzt vor URL-Clean, CustomerLogin liest) */
export const PORTAL_POST_CHECKOUT_HINT_KEY = 'portal_post_checkout_hint';

/** True wenn mindestens ein Stripe Payment Link verfügbar ist (Defaults zählen) */
export function hasStripePaymentLinks(): boolean {
    return !!(STRIPE_LINKS.STARTER || STRIPE_LINKS.PREMIUM || STRIPE_LINKS.ENTERPRISE);
}

// --- N8N VERBINDUNG (INTERNAL PROXY) ---
export const N8N_EDITOR_URL = "https://n8n.arkorbit.at";

export const N8N_CONFIG = {
  // 1. CEO-Bot (Haupt-Webhook, verarbeitet alle Text-Anfragen)
  CEO_BOT_WEBHOOK: "/webhook/38cf8499-75bb-4c90-bf0c-52ab4e3c05e6",

  // 2. ARKO – dedizierter Sales & Info Agent (Landing) mit vollem Firmenwissen.
  //    Eigener Workflow SALES_ARKO_AGENT.json (Split 10.07.2026): Off-Topic- +
  //    Competitor-Gate vor dem LLM, Output-Sanitizer danach. Portal-ARKO = /webhook/arko.
  SALES_BOT_WEBHOOK: "/webhook/sales-arko",

  // 3. Interner Assistent → läuft über CEO-Agent
  INTERNAL_ASSISTANT_WEBHOOK: "/webhook/38cf8499-75bb-4c90-bf0c-52ab4e3c05e6",

  // 4. Partner-Assistent (Affiliate) → eigener Lego-Workflow mit Claude + PB-Logging
  AFFILIATE_ASSISTANT_WEBHOOK: "/webhook/partner-arko",

  // 5. BRAIN Ingest (über nginx proxy – kein CORS)
  BRAIN_INGEST_WEBHOOK: "/webhook/brain-ingest",

  // 6. Kanzlei-Bot (Matrix-Pipeline, Health-Check in WorkflowMonitor)
  KANZLEI_BOT_WEBHOOK: "/webhook/matrix-trigger",

  // 7. Audio-Upload (KanzleiBotDemo → KUNDEN_AGENT_MATRIX)
  UPLOAD_AUDIO_WEBHOOK: "/webhook/upload-audio",

  // 8. Website Lead / Demo-Anfrage (Landing + Produktseiten → PB leads + E-Mail)
  WEBSITE_LEAD_WEBHOOK: "/webhook/website-lead",
};

/**
 * Kundenportal – Anwalts-ARKO (`ARKO_AGENT` Webhook).
 * Relativ (Default `/webhook/arko`): Request geht an dieselbe Origin wie die Web-App
 * (Firma vs. Labor je nachdem, wo das Frontend ausgeliefert wird – nginx/Traefik leitet weiter).
 * Optional absolut setzen (z. B. Labor-URL beim lokalen `npm run dev`) – Ziel muss CORS erlauben.
 *
 * Dedizierter Kunden-Edge: Der relative Default bleibt korrekt – die Portal-nginx
 * (`nginx.edge.conf.template`) proxyt `/webhook/` same-origin (`portal.<tenant>…`) an
 * den Edge-`n8n-kunden` (Goldener Pfad: Edge-n8n, nicht Firma-n8n). `VITE_PORTAL_ARKO_WEBHOOK`
 * nur absolut auf die Edge-n8n-Domain setzen, wenn der Build cross-origin ausgeliefert wird.
 */
export const PORTAL_ARKO_WEBHOOK =
  (import.meta.env.VITE_PORTAL_ARKO_WEBHOOK as string | undefined)?.trim() || "/webhook/arko";

/** Thomas-Feld-Demo: eigener ARKO-Workflow (ARKO_FIELD_AGENT → /webhook/arko-field). */
export const PORTAL_ARKO_FIELD_WEBHOOK =
  (import.meta.env.VITE_PORTAL_ARKO_FIELD_WEBHOOK as string | undefined)?.trim() ||
  '/webhook/arko-field';

/** Laufzeit: Feld-Tenant → arko-field, sonst Standard-Portal-ARKO. */
export function getPortalArkoWebhook(customerStatus?: string): string {
  return isFieldPortalTenant(undefined, customerStatus)
    ? PORTAL_ARKO_FIELD_WEBHOOK
    : PORTAL_ARKO_WEBHOOK;
}

export function isLaborPortalHostname(hostname: string): boolean {
  const host = hostname.trim().toLowerCase();
  return (
    host === 'lab.arkorbit.at' ||
    host.includes('portal-labor') ||
    /\.labor\.arkorbit\.at$/i.test(host) ||
    host.endsWith('-labor.arkorbit.at')
  );
}

/** Thomas-Feld-Demo (eigene PB + Webhooks, strikt getrennt von portal-labor). */
export function isThomasPortalHostname(hostname: string): boolean {
  return hostname.trim().toLowerCase() === 'thomas.arkorbit.at';
}

/**
 * nginx `auth_request` + portal-authz setzt `X-Arkorbit-Customer-Id` nur auf Labor/Thomas.
 * Firma (`portal.arkorbit.at` / `demo.arkorbit.at`) proxyt Webhooks ohne Sidecar — der
 * Secret-Cutover-FE sendet nur Bearer, kein Upload-Secret → Extract/ähnliche Gates → 401.
 * Bis AuthZ-Promotion: Browser-Extract dort nicht anstoßen (H1-Fallback).
 */
export function portalAuthzSidecarExpected(hostname?: string): boolean {
  const host =
    hostname?.trim() ||
    (typeof window !== 'undefined' ? window.location.hostname : '');
  if (!host) return false;
  return isLaborPortalHostname(host) || isThomasPortalHostname(host);
}

/**
 * Dedizierter Kunden-Edge: portal.<tenant>.arkorbit.at (z. B. portal.brauneis.arkorbit.at).
 * Nicht Labor (portal-labor, *.labor.arkorbit.at) und nicht Firma-Demo (demo.arkorbit.at).
 */
export function isEdgeTenantPortalHostname(hostname: string): boolean {
  const host = hostname.trim().toLowerCase();
  if (!/^portal\.[^.]+\.arkorbit\.at$/.test(host)) return false;
  if (isLaborPortalHostname(host) || isThomasPortalHostname(host) || isFirmaDemoPortalHostname(host)) {
    return false;
  }
  return true;
}

/** Kundenportal-Hostname (Firma, Labor, Feld-Demo, dedizierter Kunden-Edge). Nicht lab.arkorbit.at (CEO). */
export function isPortalHostname(hostname: string): boolean {
  const host = hostname.trim().toLowerCase();
  if (host === 'lab.arkorbit.at') return false;
  const knownPortalHosts = new Set([
    'portal.arkorbit.at',
    'demo.arkorbit.at',
    'portal-labor.arkorbit.at',
    'thomas.arkorbit.at',
  ]);
  return knownPortalHosts.has(host) || isEdgeTenantPortalHostname(host);
}

/**
 * Dev-only: Kundenportal-Login lokal ohne portal-labor Hosts-Eintrag.
 * `?forceLogin=1` oder Pfad `/login` — nur `import.meta.env.DEV`, kein Produktions-Build.
 */
export function isDevPortalLoginPreview(): boolean {
  if (!import.meta.env.DEV || typeof window === 'undefined') return false;
  const { search, pathname } = window.location;
  return (
    search.includes('forceLogin=1') ||
    pathname === '/login' ||
    pathname.startsWith('/login/')
  );
}

/** Kanonischer Pfad für Aurora-Kundenlogin (same-origin auf Portal-Hosts). */
export const PORTAL_LOGIN_PATH = '/login';

export const FIRMA_PORTAL_LOGIN_URL = 'https://portal.arkorbit.at/login';
export const LABOR_PORTAL_LOGIN_URL = 'https://portal-labor.arkorbit.at/login';

/**
 * Ziel-URL für öffentliche „Login“-Buttons (Marketing + Produktseiten).
 * Portal-Host → same-origin /login; Marketing Firma → portal.arkorbit.at; Labor → portal-labor.
 */
export function resolvePortalLoginUrl(hostname?: string): string {
  const host = (
    hostname ??
    (typeof window !== 'undefined' ? window.location.hostname : '')
  )
    .trim()
    .toLowerCase();

  if (isPortalHostname(host)) {
    return PORTAL_LOGIN_PATH;
  }

  if (import.meta.env.DEV && (host === 'localhost' || host === '127.0.0.1' || host === '')) {
    return PORTAL_LOGIN_PATH;
  }

  if (host === 'lab.arkorbit.at') {
    return LABOR_PORTAL_LOGIN_URL;
  }

  return FIRMA_PORTAL_LOGIN_URL;
}

/** Navigiert zum Kundenportal-Login (nicht CEO-Gate / Interner Zugang). */
export function navigateToPortalLogin(): void {
  if (typeof window === 'undefined') return;
  window.location.assign(resolvePortalLoginUrl());
}

/** Laufzeit: portal.<tenant>.arkorbit.at → https://pb.<tenant>.arkorbit.at */
export function resolveEdgeTenantPocketBaseUrl(hostname: string): string | null {
  const host = hostname.trim().toLowerCase();
  const m = host.match(/^portal\.([^.]+)\.arkorbit\.at$/);
  if (!m) return null;
  if (m[1] === 'labor' || host.includes('portal-labor')) return null;
  return `https://pb.${m[1]}.arkorbit.at`;
}

/** Kanonische Demo-E-Mail Firma-Stack (DEMO_ZUGANG_HANDOFF.md §5.2). */
export const FIRMA_DEMO_PORTAL_EMAIL = 'orbis-demo@arkorbit.at';

/** Verkaufs-Demo auf Firmen-Stack (portal.arkorbit.at + pb.arkorbit.at). */
export function isFirmaDemoPortalHostname(hostname: string): boolean {
  return hostname.trim().toLowerCase() === 'demo.arkorbit.at';
}

/** Labor-Feld-Tenant: Host thomas.arkorbit.at oder PB customers.status = FIELD_DEMO. */
export function isFieldPortalTenant(hostname?: string, customerStatus?: string): boolean {
  if (String(customerStatus || '').trim() === 'FIELD_DEMO') return true;
  const host =
    hostname?.trim() ||
    (typeof window !== 'undefined' ? window.location.hostname : '');
  return isThomasPortalHostname(host);
}

/** Firma-Stack-Verkaufsdemo: status=FIRMA_DEMO, Demo-E-Mail oder Host demo.arkorbit.at. */
export function isFirmaDemoPortalTenant(
  hostname?: string,
  customerStatus?: string,
  customerEmail?: string,
): boolean {
  if (String(customerStatus || '').trim() === 'FIRMA_DEMO') return true;
  if (String(customerEmail || '').trim().toLowerCase() === FIRMA_DEMO_PORTAL_EMAIL) return true;
  const host =
    hostname?.trim() ||
    (typeof window !== 'undefined' ? window.location.hostname : '');
  return isFirmaDemoPortalHostname(host);
}

/** Demo-Welten mit isoliertem NC-Prefix (kein Alpha-Fallback, kein Produktiv-Mandantenpfad). */
export function isIsolatedDemoPortalTenant(
  hostname?: string,
  customerStatus?: string,
  customerEmail?: string,
): boolean {
  return (
    isFieldPortalTenant(hostname, customerStatus) ||
    isFirmaDemoPortalTenant(hostname, customerStatus, customerEmail)
  );
}

/** Nextcloud-WebDAV-Wurzel je Tenant-Typ. */
export function getPortalNcMandantenRoot(customerStatus?: string, customerEmail?: string): string {
  if (isFirmaDemoPortalTenant(undefined, customerStatus, customerEmail)) return 'Mandanten-demo';
  if (isFieldPortalTenant(undefined, customerStatus)) return 'Mandanten-field';
  return 'Mandanten';
}

/**
 * n8n-Body-Extras: tenant=field|demo für isolierte Demo-Pfade (Upload, Share, OO, Vertrag).
 */
export function getPortalTenantN8nExtras(
  customerStatus?: string,
  customerEmail?: string,
): Record<string, string> {
  if (isFieldPortalTenant(undefined, customerStatus)) return { tenant: 'field' };
  if (isFirmaDemoPortalTenant(undefined, customerStatus, customerEmail)) return { tenant: 'demo' };
  return {};
}

/**
 * Mandanten-Root aus gespeichertem WebDAV-Pfad (Legacy `/Mandanten/` vs. Feld `/Mandanten-field/` vs. Demo `/Mandanten-demo/`).
 * Fallback = Tenant-Default, wenn der Pfad kein Praefix traegt.
 */
export function getMandantenRootFromNextcloudPath(
  nextcloudPath: string | undefined,
  customerStatus?: string,
  customerEmail?: string,
): string {
  const p = String(nextcloudPath || '').trim();
  if (p.startsWith('/Mandanten-demo/') || /^Mandanten-demo\//i.test(p)) {
    return 'Mandanten-demo';
  }
  if (p.startsWith('/Mandanten-field/') || /^Mandanten-field\//i.test(p)) {
    return 'Mandanten-field';
  }
  if (p.startsWith('/Mandanten/') || /^Mandanten\//i.test(p)) {
    return 'Mandanten';
  }
  return getPortalNcMandantenRoot(customerStatus, customerEmail);
}

/** Kundenportal: PocketBase-Basis-URL zur Laufzeit (nicht nur beim Modul-Import). */
export function getPortalPocketBaseUrl(): string {
  const fromBuild = (import.meta.env.VITE_PORTAL_POCKETBASE_URL as string | undefined)?.trim();
  if (fromBuild) return fromBuild;
  if (typeof window !== 'undefined') {
    if (isDevPortalLoginPreview()) {
      return 'https://pb-labor.arkorbit.at';
    }
    const edgePb = resolveEdgeTenantPocketBaseUrl(window.location.hostname);
    if (edgePb) return edgePb;
    if (isThomasPortalHostname(window.location.hostname)) {
      return 'https://pb-thomas.arkorbit.at';
    }
    if (isLaborPortalHostname(window.location.hostname)) {
      return 'https://pb-labor.arkorbit.at';
    }
  }
  return 'https://pb.arkorbit.at';
}

/** Storage-Key: PB-Instanz, gegen die `pb_token` ausgestellt wurde (Firma vs. Labor). */
export const PORTAL_PB_SESSION_URL_KEY = 'pb_session_base_url';

/**
 * Preference (immer localStorage): „Angemeldet bleiben“ — nur Bootstrap für neue Tabs /
 * Browser-Neustart. Die **aktive** Tab-Session liegt immer in sessionStorage.
 *
 * Session-first (Fix 07.08.2026 nach Sitze-UAT):
 * - Hard-Refresh liest zuerst sessionStorage → Tab/Fenster bleibt isoliert.
 * - remember=true spiegelt Auth zusätzlich nach localStorage (Neustart / neuer Tab).
 * - remember=false: nur sessionStorage; localStorage-Auth wird hart geleert
 *   (kein „letzter Login“ aus anderem Tab).
 * Kein `storage`-Event-Sync zwischen Tabs.
 */
export const PORTAL_REMEMBER_ME_KEY = 'arkorbit_portal_remember_me';

const PORTAL_AUTH_KEYS = ['pb_token', 'pb_customer', PORTAL_PB_SESSION_URL_KEY] as const;

export function normalizePortalPbBaseUrl(url: string): string {
  return url.trim().replace(/\/$/, '');
}

function clearPortalAuthKeysFrom(store: Storage): void {
  for (const key of PORTAL_AUTH_KEYS) {
    store.removeItem(key);
  }
}

function copyPortalAuthKeys(from: Storage, to: Storage): void {
  for (const key of PORTAL_AUTH_KEYS) {
    const value = from.getItem(key);
    if (value != null && value !== '') {
      to.setItem(key, value);
    } else {
      to.removeItem(key);
    }
  }
}

/**
 * Remember-Me-Preference lesen.
 * Unset + vorhandener localStorage-`pb_token` (ohne session-Token) → einmalig auf
 * „bleiben“ migrieren (Legacy-Anwalt-Sessions nicht hart ausloggen).
 * Hat das Tab bereits sessionStorage-Auth, gilt Preference explizit oder Default false
 * — kein aggressives Migrieren, das Multi-Account-Tabs kaputt macht.
 */
export function getPortalRememberMePreference(): boolean {
  if (typeof window === 'undefined') return false;
  const raw = localStorage.getItem(PORTAL_REMEMBER_ME_KEY);
  if (raw === '1') return true;
  if (raw === '0') return false;
  const hasSession = Boolean(sessionStorage.getItem('pb_token')?.trim());
  const hasLocal = Boolean(localStorage.getItem('pb_token')?.trim());
  if (hasLocal && !hasSession) {
    localStorage.setItem(PORTAL_REMEMBER_ME_KEY, '1');
    return true;
  }
  return false;
}

export function setPortalRememberMePreference(remember: boolean): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(PORTAL_REMEMBER_ME_KEY, remember ? '1' : '0');
}

/**
 * Store für Schreib-/Konsistenzpfade der **aktiven** Tab-Session.
 * Immer sessionStorage — localStorage ist nur Mirror bei remember=true.
 */
export function getPortalAuthStorage(): Storage {
  if (typeof window === 'undefined') {
    throw new Error('getPortalAuthStorage requires window');
  }
  return sessionStorage;
}

/**
 * Aktive Auth-Quelle: sessionStorage zuerst; nur wenn leer und remember=true,
 * aus localStorage in sessionStorage hydratisieren (einmalig pro Tab-Leben).
 */
function resolvePortalAuthStore(): Storage | null {
  if (typeof window === 'undefined') return null;
  if (sessionStorage.getItem('pb_token')?.trim()) {
    return sessionStorage;
  }
  if (
    getPortalRememberMePreference() &&
    localStorage.getItem('pb_token')?.trim()
  ) {
    copyPortalAuthKeys(localStorage, sessionStorage);
    return sessionStorage;
  }
  return sessionStorage;
}

export function getPortalPbToken(): string | null {
  const store = resolvePortalAuthStore();
  return store?.getItem('pb_token')?.trim() || null;
}

export function getPortalPbCustomerJson(): string | null {
  const store = resolvePortalAuthStore();
  return store?.getItem('pb_customer') ?? null;
}

export function writePortalPbCustomerJson(json: string): void {
  if (typeof window === 'undefined') return;
  sessionStorage.setItem('pb_customer', json);
  if (getPortalRememberMePreference()) {
    localStorage.setItem('pb_customer', json);
  } else {
    localStorage.removeItem('pb_customer');
  }
}

/**
 * Nach Login/Refresh: Token + PB-Basis-URL speichern.
 * Immer sessionStorage (Tab-Hard-Refresh hält DIESE Session).
 * `rememberMe` optional — nur bei Login setzen; Token-Refresh übernimmt die Preference.
 * remember=true: zusätzlich localStorage-Mirror.
 * remember=false: localStorage-Auth löschen (kein Cross-Tab-Leak).
 */
export function persistPortalPbSession(
  token: string,
  pbBaseUrl?: string,
  rememberMe?: boolean,
): void {
  const trimmed = token.trim();
  if (!trimmed || typeof window === 'undefined') return;
  if (typeof rememberMe === 'boolean') {
    setPortalRememberMePreference(rememberMe);
  }
  const remember = getPortalRememberMePreference();
  const stamped = normalizePortalPbBaseUrl(pbBaseUrl ?? getPortalPocketBaseUrl());
  sessionStorage.setItem('pb_token', trimmed);
  sessionStorage.setItem(PORTAL_PB_SESSION_URL_KEY, stamped);
  if (remember) {
    localStorage.setItem('pb_token', trimmed);
    localStorage.setItem(PORTAL_PB_SESSION_URL_KEY, stamped);
    const customer = sessionStorage.getItem('pb_customer');
    if (customer != null) {
      localStorage.setItem('pb_customer', customer);
    }
  } else {
    clearPortalAuthKeysFrom(localStorage);
  }
}

/** Auth-Keys in local + session löschen (Preference bleibt). */
export function clearPortalPbSession(): void {
  if (typeof window === 'undefined') return;
  clearPortalAuthKeysFrom(localStorage);
  clearPortalAuthKeysFrom(sessionStorage);
}

/**
 * Portal-Webhooks hinter dem `portal-authz`-Sidecar (nginx `auth_request`, Labor seit
 * 01.08.2026 `enforce`): Die Mandanten-Identität wird aus dem PocketBase-Token abgeleitet,
 * nicht mehr aus `customerId` im Body. Ohne Bearer antwortet der Sidecar mit 401.
 *
 * Vergleich auf Segmentgrenze wie `scripts/labor/portal_authz/portal_authz.py::_matches` —
 * `/webhook/arko` trifft `/webhook/arko/x`, aber nicht `/webhook/arko-field`.
 *
 * Diese Liste muss identisch sein zu `_DEFAULT_PROTECTED` im Sidecar, zum Compose-Default
 * `PORTAL_AUTHZ_PROTECTED_PATHS` und zum `auth_request`-Block in `nginx-labor.conf`.
 * Driftet eine davon, bekommt der Browser 401 — `scripts/n8n_workflow_lint.py` prüft das in CI.
 */
export const PORTAL_AUTHZ_PROTECTED_WEBHOOK_PATHS = [
  '/webhook/arko',
  // ARKO Streaming SSE (Labor 16.08.2026) — gleiche Tenant-Bindung wie /webhook/arko
  '/api/arko-stream',
  // Dokument-Familie (Slice 2): vorher am Shared Secret `X-Arkorbit-Portal-Upload`,
  // jetzt an der Token-Identität. OnlyOffice-Callbacks bleiben bewusst außen vor.
  '/webhook/portal-document-upload',
  '/webhook/portal-document-folder',
  '/webhook/portal-nextcloud-share-import',
  '/webhook/portal-plaintext-fetch',
  '/webhook/portal-synthesis-append',
  '/webhook/portal-frist-approval',
  '/webhook/arko-web-article-fetch',
  '/webhook/portal-flywheel-routing',
  // Slice 3 (Secret-Cutover 01.08.2026): die letzten Browser-Routen, die den
  // Shared-Secret-Header getragen haben. Damit ist kein Upload-Secret mehr im
  // Bundle — `arko-contract-finalize-pdf` bleibt draußen, das ruft nur n8n selbst.
  '/webhook/portal-onlyoffice-extract-text',
  '/webhook/arko-contract-generate',
  '/webhook/arko-schreiben-pdf',
  '/webhook/litigation-upload',
  '/webhook/litigation-analysis-jobs',
  // GET Status/Artefakt: n8n serviert die Route unter dem webhookId-Präfix, die
  // `job_id` hängt als weiteres Segment dran — der Segmentvergleich trifft sie.
  '/webhook/arkorbit-litigation-analysis-jobs-status',
] as const;

function portalWebhookPathname(webhookUrl: string): string {
  const raw = webhookUrl.trim();
  if (!raw) return '';
  try {
    const base =
      typeof window !== 'undefined' ? window.location.origin : 'https://portal.invalid';
    return new URL(raw, base).pathname;
  } catch {
    return raw.split('?')[0];
  }
}

export function isPortalAuthzProtectedWebhook(webhookUrl: string): boolean {
  const path = portalWebhookPathname(webhookUrl).replace(/\/$/, '');
  if (!path) return false;
  return PORTAL_AUTHZ_PROTECTED_WEBHOOK_PATHS.some(
    (protectedPath) => path === protectedPath || path.startsWith(`${protectedPath}/`),
  );
}

/**
 * Bearer **nur** für die geschützten Portal-Webhooks. Bewusst nicht pauschal auf jeden
 * `/webhook/`-Aufruf: `lab.arkorbit.at` liegt hinter Traefik-Basic-Auth (`ceo-auth`), und ein
 * Bearer auf den Landing-Routen (`/webhook/sales-arko`, `/webhook/partner-arko`) würde die
 * Basic-Credentials des Browsers verdrängen — genau die 401-Falle aus dem OnlyOffice-Bridge-Pfad.
 */
export function portalAuthzHeaders(webhookUrl: string): Record<string, string> {
  if (typeof window === 'undefined') return {};
  if (!isPortalAuthzProtectedWebhook(webhookUrl)) return {};
  const token = getPortalPbToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Liegt ein PB-Token vor, mit dem sich die geschützten Portal-Webhooks aufrufen lassen?
 *
 * Ersetzt die früheren `if (!PORTAL_DOCUMENT_UPLOAD_SECRET)`-Abfragen: nicht mehr
 * „ist das Secret eingebaut", sondern „ist dieser Browser angemeldet". Ohne Token
 * antwortet der Sidecar 401 — die Meldung soll das sagen, statt einen Build-Fehler
 * zu behaupten.
 */
export function hasPortalAuthzToken(): boolean {
  if (typeof window === 'undefined') return false;
  return Boolean(getPortalPbToken());
}

/** Deutsche Login-Fehlermeldung aus PocketBase-Antwort (customers/auth-with-password). */
export function formatPortalLoginError(
  status: number,
  payload: unknown,
  pbBaseUrl?: string,
): string {
  const msg =
    typeof payload === 'object' && payload !== null && 'message' in payload
      ? String((payload as { message?: string }).message ?? '')
      : '';
  const lower = msg.toLowerCase();

  if (status >= 500) {
    return 'Der Anmeldeserver ist vorübergehend nicht erreichbar. Bitte in ein paar Minuten erneut versuchen.';
  }
  if (status === 429) {
    return 'Zu viele Anmeldeversuche. Bitte kurz warten und es erneut versuchen.';
  }
  if (status === 400) {
    if (lower.includes('loading the submitted data') || lower.includes('validation')) {
      return 'Bitte E-Mail und Passwort vollständig eingeben.';
    }
    if (lower.includes('failed to authenticate') || lower.includes('invalid login credentials')) {
      let pbHint = '';
      if (typeof window !== 'undefined') {
        if (isThomasPortalHostname(window.location.hostname)) {
          pbHint =
            ' Hinweis: Thomas-Feld-Portal nutzt Kanzlei-Zugangsdaten auf pb-thomas — nicht pb-labor oder die PB-Admin-Konsole (/_/).';
        } else if (isLaborPortalHostname(window.location.hostname)) {
          pbHint =
            ' Hinweis: Labor-Portal nutzt Kanzlei-Zugangsdaten auf pb-labor — nicht das Passwort der PocketBase-Admin-Konsole (/_/).';
        }
      }
      return `E-Mail oder Passwort ungültig.${pbHint}`;
    }
  }
  if (status === 404) {
    return 'Anmeldedienst nicht gefunden. Bitte Seite neu laden oder Support kontaktieren.';
  }
  if (status === 0 || status === 503) {
    const host = pbBaseUrl?.replace(/^https?:\/\//, '') ?? 'PocketBase';
    return `Verbindung zu ${host} fehlgeschlagen. Netzwerk oder Sicherheitseinstellungen (Firewall/VPN) prüfen.`;
  }
  if (msg.trim()) {
    return `Anmeldung fehlgeschlagen: ${msg}`;
  }
  return 'Anmeldung fehlgeschlagen. Bitte Zugangsdaten prüfen.';
}

/** PocketBase impersonate/Passkey-Token: gültig, aber auth-refresh antwortet 403 „not refreshable“. */
export function isPbAuthRefreshNonRenewable(status: number, payload: unknown): boolean {
  if (status !== 403) return false;
  const msg =
    typeof payload === 'object' && payload !== null && 'message' in payload
      ? String((payload as { message?: string }).message ?? '').toLowerCase()
      : '';
  return msg.includes('not refreshable');
}

/** true = Session passt zur aktuellen Portal-PB-URL; false = Token von anderer Instanz. */
export function isPortalPbSessionConsistent(): boolean {
  if (typeof window === 'undefined') return true;
  const store = resolvePortalAuthStore();
  if (!store) return true;
  const token = store.getItem('pb_token')?.trim();
  if (!token) return true;
  const stored = store.getItem(PORTAL_PB_SESSION_URL_KEY)?.trim();
  if (!stored) {
    // Legacy-Sessions ohne Stempel: auf Labor-/Feld-Portal PB-Instanz erzwingen (Firma-Token → leere Liste / 404).
    if (isThomasPortalHostname(window.location.hostname)) {
      return normalizePortalPbBaseUrl(getPortalPocketBaseUrl()).includes('pb-thomas.arkorbit.at')
        ? false
        : true;
    }
    if (isLaborPortalHostname(window.location.hostname)) {
      return normalizePortalPbBaseUrl(getPortalPocketBaseUrl()).includes('pb-labor.arkorbit.at')
        ? false
        : true;
    }
    return true;
  }
  return stored === normalizePortalPbBaseUrl(getPortalPocketBaseUrl());
}

/**
 * Kundenportal: PocketBase-API (Labor: pb-labor; Build-Override via VITE_PORTAL_POCKETBASE_URL).
 * @deprecated API-Calls: `getPortalPocketBaseUrl()` — nicht diese Konstante (Modul-Import-Zeitpunkt).
 */
export const PORTAL_POCKETBASE_URL = getPortalPocketBaseUrl();

if (import.meta.env.DEV && typeof window !== 'undefined') {
  const looksLaborPortal = isLaborPortalHostname(window.location.hostname);
  const pbExplicit = Boolean((import.meta.env.VITE_PORTAL_POCKETBASE_URL as string | undefined)?.trim());
  if (looksLaborPortal && !pbExplicit) {
    // eslint-disable-next-line no-console -- nur Dev-Hinweis, kein Secret
    console.warn(
      '[Portal] Labor-Hostname ohne VITE_PORTAL_POCKETBASE_URL im Build — Laufzeit-Fallback pb-labor.arkorbit.at aktiv (siehe .env.example).',
    );
  }
}

/** PB-Collection für „Meine Protokolle“ (einheitlich `portal_documents` mit display_name + document_url). */
export const PORTAL_PROTOKOLLE_COLLECTION =
  (import.meta.env.VITE_PORTAL_PROTOKOLLE_COLLECTION as string | undefined)?.trim() || "portal_documents";

/**
 * OnlyOffice Document Server – öffentliche Basis-URL nur für Embed (Vite Build-Zeit).
 * Leer: Portal „Dokument (Alpha)“ zeigt Hinweis (Build/Deploy) statt iframe.
 */
export const ONLYOFFICE_DOCUMENT_SERVER_URL =
  (import.meta.env.VITE_ONLYOFFICE_DOCUMENT_SERVER_URL as string | undefined)?.trim() || "";

/**
 * POST-Ziel für n8n **ONLYOFFICE_ISSUE_JWT** (`/webhook/onlyoffice-issue-jwt`).
 * Relativ (z. B. `/webhook/onlyoffice-issue-jwt`) → gleiche Origin wie die Web-App (nginx → n8n).
 * Alternativ Alias **VITE_ONLYOFFICE_EDITOR_INIT_URL** (gleiche Semantik).
 */
export const ONLYOFFICE_ISSUE_JWT_URL =
  (import.meta.env.VITE_ONLYOFFICE_ISSUE_JWT_URL as string | undefined)?.trim() ||
  (import.meta.env.VITE_ONLYOFFICE_EDITOR_INIT_URL as string | undefined)?.trim() ||
  '';

/**
 * Optional: Bridge-Token für n8n ONLYOFFICE_ISSUE_JWT — im Browser als Header
 * `X-Arkorbit-Onlyoffice-Bridge` (nicht `Authorization: Bearer`, sonst kollidiert CEO-Basic auf portal-labor).
 */
export const ONLYOFFICE_JWT_BRIDGE_TOKEN =
  (import.meta.env.VITE_ONLYOFFICE_JWT_BRIDGE_TOKEN as string | undefined)?.trim() || '';

/**
 * Öffentliche **callbackUrl** für OnlyOffice (n8n **ONLYOFFICE_SAVE_CALLBACK**), z. B.
 * `https://portal-labor…/webhook/onlyoffice-save-callback`. Relativ (`/webhook/…`) → Origin zur Laufzeit.
 * Leer lassen = kein `editorConfig.callbackUrl` (Demo ohne Save-Webhook).
 */
export const ONLYOFFICE_CALLBACK_URL =
  (import.meta.env.VITE_ONLYOFFICE_CALLBACK_URL as string | undefined)?.trim() || '';

/**
 * OnlyOffice-Chrome (Toolbar/Rahmen) — nicht die weiße Seite.
 * Default `theme-arkorbit-cursor`: dunkles ARKORBIT-Charcoal + Violet/Blau-Akzent (Labor DS deployen).
 * Fallback `theme-arkorbit`: helles Blau-Slate (Legacy).
 * Liegt unter `10_Server_Config/onlyoffice/themes/`.
 */
export const ONLYOFFICE_UI_THEME =
  (import.meta.env.VITE_ONLYOFFICE_UI_THEME as string | undefined)?.trim() || 'theme-arkorbit-cursor';

/** editorConfig.customization — geht mit in JWT (ONLYOFFICE_ISSUE_JWT). */
export function buildOnlyofficeEditorCustomization(uiTheme: string = ONLYOFFICE_UI_THEME): Record<string, unknown> {
  return {
    uiTheme,
    integrationMode: 'embed',
    toolbarHideFileName: false,
    help: false,
    feedback: { visible: false },
    goback: { visible: false },
    /** DS-Autosave (status 6) — entlastet Schließen/Doc-Wechsel vs. nur status 2 beim Unmount. */
    autosave: true,
    /**
     * Rechtschreibprüfung beim Laden aktiv (OnlyOffice 8.2: `features.spellcheck`,
     * Altfeld `customization.spellcheck` deprecated). Die Prüf-Sprache ist eine
     * Dokument-Eigenschaft, nicht über die DocsAPI setzbar — neue Dokumente bringen
     * de-DE aus der Blank-Vorlage (siehe components/portal/blankDocx.ts) mit.
     */
  features: {
      spellcheck: true,
      /** OnlyOffice 8.2: Line-Tabs + Toolbar-Hintergrund — vermeidet weiße Tab-Flächen bei Dark-Theme. */
      tabStyle: 'line',
      tabBackground: 'toolbar',
    },
  };
}

/**
 * Stirling-PDF — leichter PDF-Werkzeugkasten (merge/split/compress/sign/…), zusätzlich zu OnlyOffice.
 * Build-Override via **VITE_STIRLING_PDF_URL**; sonst Laufzeit-Fallback je Hostname
 * (Labor/Thomas → `https://pdf-labor.arkorbit.at`). Wird im Portal in einem neuen Tab geöffnet
 * (kein iframe → keine CSP-frame-src-Erweiterung nötig). Leer = Portal zeigt Hinweis statt Link.
 */
export const STIRLING_PDF_URL =
  (import.meta.env.VITE_STIRLING_PDF_URL as string | undefined)?.trim() || '';

/** Laufzeit-Auflösung der Stirling-PDF-Basis-URL (Build-Override → Labor/Thomas-Fallback). */
export function getStirlingPdfUrl(): string {
  if (STIRLING_PDF_URL) return STIRLING_PDF_URL.replace(/\/$/, '');
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (isThomasPortalHostname(host) || isLaborPortalHostname(host)) {
      return 'https://pdf-labor.arkorbit.at';
    }
  }
  return '';
}

/**
 * Firma-Portal: berechtigter Shared-Demo-Admin sendet einen temporaeren
 * Demo-Zugang per n8n/Brevo. Der Workflow prueft den PB-Bearer selbst und
 * akzeptiert nur `portal.arkorbit.at` + `FIRMA_DEMO` + it_admin/is_primary.
 */
export const PORTAL_DEMO_ACCESS_URL =
  (import.meta.env.VITE_PORTAL_DEMO_ACCESS_URL as string | undefined)?.trim() ||
  '/webhook/portal-demo-access';

/**
 * Portal: Word-Datei für OnlyOffice (n8n **PORTAL_DOCUMENT_UPLOAD**).
 * Multipart `file` + `customerId`; Authentifizierung über `portalAuthzHeaders()`.
 */
export const PORTAL_DOCUMENT_UPLOAD_URL =
  (import.meta.env.VITE_PORTAL_DOCUMENT_UPLOAD_URL as string | undefined)?.trim() ||
  '/webhook/portal-document-upload';

/**
 * Portal: Ordner anlegen oder Freigabe-Link erzeugen (n8n **PORTAL_DOCUMENT_FOLDER**).
 * POST JSON `{ action: 'create'|'share', customerId, folderPath?, permissions? }`;
 * Authentifizierung über `portalAuthzHeaders()`.
 */
export const PORTAL_DOCUMENT_FOLDER_URL =
  (import.meta.env.VITE_PORTAL_DOCUMENT_FOLDER_URL as string | undefined)?.trim() ||
  '/webhook/portal-document-folder';

/**
 * Portal: Klartext (.txt/.md/…) aus Nextcloud ohne Browser-CORS auf 303-Redirects.
 * POST JSON `{ document_url }` zu n8n **PORTAL_PLAINTEXT_FETCH**; Bearer via `portalAuthzHeaders()`.
 */
export const PORTAL_PLAINTEXT_FETCH_URL =
  (import.meta.env.VITE_PORTAL_PLAINTEXT_FETCH_URL as string | undefined)?.trim() ||
  '/webhook/portal-plaintext-fetch';

/**
 * Portal: Öffentliche Web-Artikel (Allowlist AT/EU) serverseitig laden (n8n **ARKO_WEB_ARTICLE_FETCH**).
 * POST JSON `{ url }`; Bearer via `portalAuthzHeaders()`.
 */
export const ARKO_WEB_ARTICLE_FETCH_URL =
  (import.meta.env.VITE_ARKO_WEB_ARTICLE_FETCH_URL as string | undefined)?.trim() ||
  '/webhook/arko-web-article-fetch';

/**
 * Portal H2: Volltext aus OnlyOffice ConvertService via n8n **PORTAL_ONLYOFFICE_EXTRACT_TEXT** (Vertrag siehe
 * `docs/handoffs/ONLYOFFICE_ARKO_INTEGRATION_STATUS.md` §3a). Leer (`''`) = OnlyOffice-Pfad bleibt beim
 * H1-Verhalten (Metadaten + Hinweis im `documentContext`), kein Fetch. Gesetzt: Bearer via
 * `portalAuthzHeaders()` wie **PORTAL_PLAINTEXT_FETCH** / Upload.
 */
export const PORTAL_ONLYOFFICE_EXTRACT_TEXT_URL =
  (import.meta.env.VITE_PORTAL_ONLYOFFICE_EXTRACT_TEXT_URL as string | undefined)?.trim() ||
  '/webhook/portal-onlyoffice-extract-text';

/**
 * Portal Slice 1.3b V2: Synthese-Entwurf serverseitig ans .docx anhängen (n8n **PORTAL_SYNTHESIS_APPEND**).
 * POST JSON `{ text, customerId, nextcloud_path, display_name? }`; Bearer via
 * `portalAuthzHeaders()`. Ersetzt OnlyOffice Automation API (createConnector — nur Developer Edition).
 */
export const PORTAL_SYNTHESIS_APPEND_URL =
  (import.meta.env.VITE_PORTAL_SYNTHESIS_APPEND_URL as string | undefined)?.trim() ||
  '/webhook/portal-synthesis-append';

/**
 * Portal: Öffentlichen Nextcloud-Freigabe-Link serverseitig laden (n8n **PORTAL_NEXTCLOUD_SHARE_IMPORT**).
 * POST JSON `{ shareUrl, customerId }`; Bearer via `portalAuthzHeaders()` wie beim Upload.
 */
export const PORTAL_NEXTCLOUD_SHARE_IMPORT_URL =
  (import.meta.env.VITE_PORTAL_NEXTCLOUD_SHARE_IMPORT_URL as string | undefined)?.trim() ||
  '/webhook/portal-nextcloud-share-import';

/**
 * Verfahrensmotor Slice 1: Approval-Card → n8n **PORTAL_FRIST_APPROVAL** → Motor → PB `portal_fristen`.
 * POST JSON siehe `components/portal/verfahrensmotor/fristApprovalN8nHooks.ts`.
 * Bearer via `portalAuthzHeaders()` (wie Dokument-Upload).
 */
export const PORTAL_FRIST_APPROVAL_URL =
  (import.meta.env.VITE_PORTAL_FRIST_APPROVAL_URL as string | undefined)?.trim() ||
  '/webhook/portal-frist-approval';

/**
 * Flywheel 4.4 (nur Labor): falschen Reasoning-Trace-Pfad als reine Routing-
 * Metadaten an n8n melden. Der zusätzliche Laufzeit-Host-Guard sitzt in
 * `portalFlywheelRouting.ts`; Produktion baut das Feature nicht aktiv.
 */
export const PORTAL_FLYWHEEL_ROUTING_URL =
  (import.meta.env.VITE_PORTAL_FLYWHEEL_ROUTING_URL as string | undefined)?.trim() ||
  '/webhook/portal-flywheel-routing';

/**
 * ARKO Vertragsgenerator (Alpha/Pilot, Stream C+D). POST JSON
 * `{ templateType, fields, customerId, display_name? }` an n8n **ARKO_CONTRACT_GENERATE**;
 * Bearer via `portalAuthzHeaders()` (gleiche Tenant-Bindung wie Upload).
 * Antwort identisch zu **PORTAL_DOCUMENT_UPLOAD** (`document_url`, `nextcloud_path`, `display_name`, `pb_record_id`).
 * **Leer (`''`) = Feature aus** (kein Sidebar-Eintrag, kein In-Chat-Slot-Filling). Labor-only.
 */
export const ARKO_CONTRACT_GENERATE_URL =
  (import.meta.env.VITE_ARKO_CONTRACT_GENERATE_URL as string | undefined)?.trim() || '';

/** True, wenn der Vertragsgenerator gebaut/aktiv ist (URL gesetzt). */
export function isContractGeneratorEnabled(): boolean {
  return ARKO_CONTRACT_GENERATE_URL.length > 0;
}

/**
 * ARKO Schreiben-/Schriftsatz-Entwurf als PDF (ARKO V2 Stream 3). POST JSON
 * `{ text, customerId, display_name?, kanzlei?, tenant?/customerStatus? }` an n8n
 * **ARKO_SCHREIBEN_PDF** (Gotenberg HTML->PDF); Bearer via `portalAuthzHeaders()`.
 * Antwort: `{ ok, pdf_url, nextcloud_path, display_name, pdf_bytes }` —
 * `pdf_url` nur intern als Portal-`document_url` (Viewer), nie als Chat-Direktlink.
 * **Leer (`''`) = PDF-Export-Button aus.** Labor-only.
 */
export const ARKO_SCHREIBEN_PDF_URL =
  (import.meta.env.VITE_ARKO_SCHREIBEN_PDF_URL as string | undefined)?.trim() || '';

/** True, wenn der Schreiben-PDF-Export gebaut/aktiv ist (URL gesetzt). */
export function isSchreibenPdfExportEnabled(): boolean {
  return ARKO_SCHREIBEN_PDF_URL.length > 0;
}

/**
 * MEMCORE Modul B (Labor, Premium): Curator-Webhook der einzigen Schreibstraße
 * (n8n **MEMCORE_CURATOR_LABOR**, SKALIERUNG §7.3/§7.8). POST JSON mit
 * `aktion: proposal | vorschau | supersede | tombstone | recall` + `kanzlei_id`/`user_id`.
 * Relativ (z. B. `/webhook/memcore-curator-test`) → gleiche Origin (nginx → n8n-labor)
 * oder volle Labor-URL. **Leer (`''`) = Feature aus**: kein „Mein Gedächtnis“,
 * kein Session-Speichern/Nudge/Daumen, kein Onboarding-Interview. Labor-only.
 */
export const MEMCORE_CURATOR_URL =
  (import.meta.env.VITE_MEMCORE_CURATOR_URL as string | undefined)?.trim() || '';

/** Feld-Demo: MEMCORE_FIELD_CURATOR_LABOR → /webhook/memcore-field-curator, Collection memcore_field. */
export const MEMCORE_FIELD_CURATOR_URL =
  (import.meta.env.VITE_MEMCORE_FIELD_CURATOR_URL as string | undefined)?.trim() ||
  '/webhook/memcore-field-curator';

/** Laufzeit: Feld-Tenant → Field-Curator-Webhook. */
export function getMemcoreCuratorUrl(customerStatus?: string): string {
  if (isFieldPortalTenant(undefined, customerStatus)) {
    return MEMCORE_FIELD_CURATOR_URL;
  }
  return MEMCORE_CURATOR_URL;
}

/** True, wenn das Anwalts-Gedächtnis (MEMCORE Modul B) aktiv ist (URL gesetzt). */
export function isMemcoreEnabled(customerStatus?: string): boolean {
  return getMemcoreCuratorUrl(customerStatus).length > 0;
}

/**
 * Portal: Litigation Engine (Orbis Premium) — n8n **LITIGATION_ENGINE**.
 * Multipart (PDF `file`/`file_N`) oder JSON `{ optIn, schriftsatzText, customerId, kanzlei }`.
 * Relativ (Default `/webhook/litigation-upload`) → gleiche Origin (nginx → n8n-labor auf portal-labor).
 */
export const PORTAL_LITIGATION_UPLOAD_URL =
  (import.meta.env.VITE_PORTAL_LITIGATION_UPLOAD_URL as string | undefined)?.trim() ||
  '/webhook/litigation-upload';

/**
 * Portal ↔ JUDICATURE Paket 2B: synthetischen Labor-Demojob anlegen.
 * Authentifizierung über dieselbe Token-Bindung wie Dokumente (`portalAuthzHeaders()`).
 */
export const PORTAL_LITIGATION_JOB_SUBMIT_URL =
  (import.meta.env.VITE_PORTAL_LITIGATION_JOB_SUBMIT_URL as string | undefined)?.trim() ||
  '/webhook/litigation-analysis-jobs';

/**
 * n8n-Statuspfad mit Webhook-ID-Präfix (Labor-Ist 23.07.2026).
 * `{job_id}` wird im Client URL-kodiert ersetzt; `?artifact=docx|pdf` liefert
 * ausschließlich synthetische Demo-Artefakte.
 */
export const PORTAL_LITIGATION_JOB_STATUS_URL_TEMPLATE =
  (import.meta.env.VITE_PORTAL_LITIGATION_JOB_STATUS_URL_TEMPLATE as string | undefined)?.trim() ||
  '/webhook/arkorbit-litigation-analysis-jobs-status/litigation-analysis-jobs/{job_id}';

function parseCommaSeparatedHosts(raw: string | undefined): string[] {
  if (!raw?.trim()) return [];
  return raw
    .split(/[,;\s]+/)
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
}

/** Hostname aus Basis-URL (https://host/…) — bei Ungültig null */
function hostnameFromOptionalUrl(urlRaw: string | undefined): string | null {
  const t = urlRaw?.trim();
  if (!t) return null;
  try {
    const u = new URL(t);
    return u.hostname ? u.hostname.toLowerCase() : null;
  } catch {
    return null;
  }
}

/**
 * Erlaubte Hostnamen für Freigabe-URLs (Paste im Portal). Leeres Set: Tab „Nextcloud“ bleibt aus bis Allowlist gesetzt.
 * **VITE_NEXTCLOUD_ALLOWED_HOSTS** (komma-separiert) plus optional **VITE_NEXTCLOUD_PUBLIC_BASE** / **VITE_NEXTCLOUD_URL**.
 */
export function getPortalNextcloudShareAllowedHosts(): Set<string> {
  const set = new Set<string>();
  parseCommaSeparatedHosts(import.meta.env.VITE_NEXTCLOUD_ALLOWED_HOSTS as string | undefined).forEach((h) =>
    set.add(h),
  );
  const fromBase =
    hostnameFromOptionalUrl(import.meta.env.VITE_NEXTCLOUD_PUBLIC_BASE as string | undefined) ||
    hostnameFromOptionalUrl(import.meta.env.VITE_NEXTCLOUD_URL as string | undefined);
  if (fromBase) set.add(fromBase);
  return set;
}

export type NextcloudSharePasteValidation =
  | { ok: true; href: string }
  | { ok: false; error: string };

/** Validierung vor POST an n8n: nur http(s), Host in Allowlist */
export function validateNextcloudSharePasteUrl(
  raw: string,
  allowedHosts: Set<string>,
): NextcloudSharePasteValidation {
  const t = raw.trim();
  if (!t) return { ok: false, error: 'Bitte einen öffentlichen Freigabe-Link einfügen.' };
  if (!/^https?:\/\//i.test(t)) {
    return { ok: false, error: 'Nur Links mit http:// oder https:// sind erlaubt.' };
  }
  let u: URL;
  try {
    u = new URL(t);
  } catch {
    return { ok: false, error: 'Der Link ist keine gültige URL.' };
  }
  if (u.protocol !== 'http:' && u.protocol !== 'https:') {
    return { ok: false, error: 'Nur http(s)-URLs sind erlaubt.' };
  }
  const host = u.hostname.toLowerCase();
  if (!allowedHosts.has(host)) {
    return {
      ok: false,
      error:
        'Diese Nextcloud-Adresse ist im Portal nicht freigegeben (Host fehlt in VITE_NEXTCLOUD_ALLOWED_HOSTS bzw. PUBLIC_BASE).',
    };
  }
  return { ok: true, href: u.href };
}

/**
 * Relativen Pfad oder vollständige URL für fetch() auflösen (Browser).
 * Shared Labor-dist kann absolute portal-labor-Webhook-URLs einbacken — auf thomas.arkorbit.at
 * wird der /webhook/-Pfad gegen die aktuelle Origin aufgelöst (same-origin).
 */
export function resolveOnlyofficeIssueJwtUrl(pathOrUrl: string): string {
  const t = pathOrUrl.trim();
  if (!t) return t;
  if (/^https?:\/\//i.test(t)) {
    try {
      const u = new URL(t);
      if (u.pathname.startsWith('/webhook/') && typeof window !== 'undefined') {
        return `${window.location.origin}${u.pathname}`;
      }
    } catch {
      /* fall through */
    }
    return t;
  }
  if (typeof window === 'undefined') return t;
  return t.startsWith('/') ? `${window.location.origin}${t}` : `${window.location.origin}/${t.replace(/^\//, '')}`;
}

export const INITIAL_LEADS: Lead[] = [];
export const MOCK_CHART_DATA = [
  { name: 'Start', value: 0 },
  { name: 'Woche 1', value: 0 },
  { name: 'Woche 2', value: 0 },
  { name: 'Woche 3', value: 0 },
];

export const SYSTEM_STATUS: BotStatus[] = [
  {
    id: 'server-core',
    name: 'ARKORBIT VPS Frankfurt',
    type: 'PROXY',
    status: 'Online',
    version: 'Traefik v2.11',
    cpuLoad: 12,
    memoryUsage: '1.2 GB',
    uptime: '99.99%'
  },
  {
    id: 'bot-1',
    name: 'ARKORBIT Sales Agent',
    type: 'WORKFLOW',
    status: 'Online',
    version: 'n8n v2.9.2',
    cpuLoad: 45,
    memoryUsage: '680 MB',
    uptime: '100.0%'
  },
  {
    id: 'bot-2',
    name: 'SafeBox Storage',
    type: 'STORAGE',
    status: 'Online',
    version: 'Nextcloud Hub 8',
    cpuLoad: 28,
    memoryUsage: '2.1 GB',
    uptime: '100.0%'
  }
];
